import _ProgressViewWords_learner from "../__generated__/ProgressViewWords_learner.graphql";
import _ProgressViewWindow_learner from "../__generated__/ProgressViewWindow_learner.graphql";
import _ProgressViewQuery from "../__generated__/ProgressViewQuery.graphql";
import { Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { Link } from 'react-router-dom';
import { t } from '../i18n/t';
import { localDay } from '../lessons/localDay.ts';
import { adultCharts, kidCharts, windowStart } from './charts.ts';
import { ProgressCharts } from './ProgressCharts.tsx';
import { QuietBoundary } from '../components/QuietBoundary';
import { MomentsList } from './Moments';
import { ActivityCalendar, NextUpCard, PatternList, TotalsTiles } from './ProgressSummary';
import { adultProgress, kidProgress } from './progress.ts';
import { activityGrid, nextPattern, patternRows, totals } from './progressOverview.ts';
import { whenLabel } from './sessions.ts';
import { useWholeConnection } from './useWholeConnection.ts';
import { registerFor } from '../profile/register';
import { Sticker } from '../scrapbook/Sticker';
import { isStickerSlug } from '../scrapbook/stickers.ts';
import type { ProgressViewQuery as ProgressViewQueryType } from '../__generated__/ProgressViewQuery.graphql';
import type { ProgressViewWindowPaginationQuery } from '../__generated__/ProgressViewWindowPaginationQuery.graphql';
import type { ProgressViewWindow_learner$key } from '../__generated__/ProgressViewWindow_learner.graphql';
import type { ProgressViewWordsPaginationQuery } from '../__generated__/ProgressViewWordsPaginationQuery.graphql';
import type { ProgressViewWords_learner$key } from '../__generated__/ProgressViewWords_learner.graphql';

/**
 * One query for both registers.
 *
 * The selection cannot vary by register, because the register is read from
 * this same `me` -- the query would have to be built from its own answer. The
 * projection in `progress.ts` is the boundary instead: spec §3's rule is about
 * what reaches the DOM, and a field sitting unread in the Relay store is not
 * something a child can be shown.
 */
const ProgressQuery = (_ProgressViewQuery.hash && _ProgressViewQuery.hash !== "39086a02fca39ffa1cab299a75ab64d3" && console.error("The definition of 'ProgressViewQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _ProgressViewQuery);

// The charts' twelve weeks, by date. Its own field rather than a wider
// sessions list: the list and the streak keep the shape the progress design
// gave them. Fragments rather than fields of the query above only because a
// connection has to be one to page.
const WindowFragment = (_ProgressViewWindow_learner.hash && _ProgressViewWindow_learner.hash !== "330cfeb6026bf369835811e4970d667d" && console.error("The definition of 'ProgressViewWindow_learner' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _ProgressViewWindow_learner);
const WordsFragment = (_ProgressViewWords_learner.hash && _ProgressViewWords_learner.hash !== "bb239ac6a6f8b1229f9bf702936e58ab" && console.error("The definition of 'ProgressViewWords_learner' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _ProgressViewWords_learner);

// The server's `MAX_PAGE_SIZE`; asking for more is silently asking for this.
const PAGE = 100;

/** Patterns on the overview; the rest are a link away in Practice. */
const PATTERN_ROWS = 4;

/**
 * Renders nothing at zero. The one-day floor that decides "no streak" lives
 * in `conversationStreak` (`progress.ts`), not here -- this only renders what
 * it is given.
 */
function Streak({
  days
}: {
  days: number;
}) {
  if (days === 0) return null;
  return <p className="text-base-content/70">{t('progress-streak', {
      days
    })}</p>;
}

/**
 * The way into the scrapbook, at the top of the kid screen (kid memory design
 * §3.3), showing the newest page's sticker. Shown with no pages too, a book
 * held faded, because the scrapbook's empty state is what tells a child how
 * a first page arrives.
 */
function ScrapbookCard({
  sticker
}: {
  sticker: string | null;
}) {
  return <Link to="/scrapbook" className="bg-base-100 rounded-box flex min-h-(--hit-min) items-center gap-4 p-4 shadow-md hover:shadow-lg">
      {sticker !== null ? <Sticker slug={isStickerSlug(sticker) ? sticker : 'chat'} className="size-16 shrink-0" /> : <Sticker slug="book" className="size-16 shrink-0 opacity-50" />}
      <span className="flex flex-col">
        <span className="text-xl font-bold">{t('scrapbook-title')}</span>
        <span className="text-base-content/70">{t('scrapbook-card-open')}</span>
      </span>
    </Link>;
}
function Phrases({
  phrases,
  title
}: {
  phrases: readonly string[];
  title: string;
}) {
  if (phrases.length === 0) return null;
  return <section className="flex flex-col gap-2">
      <h2 className="font-semibold">{title}</h2>
      <ul className="flex flex-wrap gap-2">
        {phrases.map(phrase => <li key={phrase} className="badge badge-success badge-lg" lang="en" dir="ltr">
            {phrase}
          </li>)}
      </ul>
    </section>;
}
export function ProgressView() {
  // Once per mount: the window is computed from it, and a `since` that moved
  // on every render would be a new query variable, and a refetch, each time.
  const [now] = useState(() => new Date());
  const data = useLazyLoadQuery<ProgressViewQueryType>(ProgressQuery, {
    since: windowStart(now).toISOString(),
    from: localDay(windowStart(now)),
    to: localDay(now)
  });
  const learner = data.me?.learner;
  const register = registerFor(learner?.defaultCharacter?.audience);
  const sessions = learner?.sessions?.nodes ?? [];
  const items = learner?.practiceItems ?? [];
  const windowPages = usePaginationFragment<ProgressViewWindowPaginationQuery, ProgressViewWindow_learner$key>(WindowFragment, learner ?? null);
  const wordPages = usePaginationFragment<ProgressViewWordsPaginationQuery, ProgressViewWords_learner$key>(WordsFragment, learner ?? null);
  const windowLoaded = useWholeConnection(windowPages, PAGE);
  const wordsLoaded = useWholeConnection(wordPages, PAGE);
  const chartInput = {
    sessions: (windowPages.data?.window?.edges ?? []).flatMap(e => e?.node ? [e.node] : []),
    words: (wordPages.data?.vocabularyItems?.edges ?? []).flatMap(e => e?.node ? [{
      firstMetAt: e.node.firstMetAt as string,
      source: e.node.source
    }] : []),
    items,
    now
  };
  // Drawn only once both windows are whole: until then the charts would show
  // a trend the next page changes.
  const hasWindow = windowLoaded && wordsLoaded && chartInput.sessions.length > 0;
  if (register === 'kid') {
    const view = kidProgress(sessions, now);
    return <div className="flex flex-col gap-6 py-6">
        <h1 className="text-2xl font-bold">{t('progress-kid-title')}</h1>
        <ScrapbookCard sticker={learner?.newestPage[0]?.sticker ?? null} />
        <QuietBoundary>
          <Suspense fallback={null}>
            <MomentsList register="kid" />
          </Suspense>
        </QuietBoundary>
        {view.conversations === 0 ? <p className="text-base-content/60">{t('progress-kid-empty')}</p> : <>
            <section>
              <p className="text-4xl font-bold">{view.conversations}</p>
              <p className="text-base-content/70">
                {/* "recent", matching the adult copy below: `conversations`
                    is bounded by the query's `first: 20` (progress.ts), and
                    a learner with fifty conversations told they've had
                    twenty is a number they can disprove. */}
                {t('progress-kid-recent', {
              count: view.conversations
            })}
              </p>
              <Streak days={view.streak} />
            </section>
            <Phrases phrases={view.phrases} title={t('progress-kid-phrases')} />
            {hasWindow && <ProgressCharts charts={kidCharts(chartInput)} register="kid" />}
          </>}
      </div>;
  }

  // No `<h1>` and no padding of its own: `ProgressLayout` puts both above
  // the tabs, as `AccountView` does.
  //
  // Next up, then the window's totals and its days, then the patterns and
  // what was got right; the week-by-week charts stay, folded, for anyone
  // who wants the trend. Everything counted comes from the window paged in
  // whole, never from the capped `sessions(first: 20)`.
  const view = adultProgress(items, sessions, now);
  const lessonDays = learner?.lessonDays ?? [];
  const rows = patternRows(items, chartInput.sessions, PATTERN_ROWS);
  const next = nextPattern(items, sessions);
  const lastTalk = sessions.reduce<string | null>((latest, s) => !latest || s.startedAt > latest ? s.startedAt : latest, null);
  if (view.conversations === 0 && items.length === 0 && lessonDays.length === 0) {
    return <div className="flex flex-col gap-6">
        <NextUpCard next={{
        kind: 'talk'
      }} rows={[]} lastTalk={null} />
        <p className="text-base-content/60">{t('progress-empty')}</p>
      </div>;
  }
  const grid = activityGrid(chartInput.sessions, lessonDays, now);
  return <div className="flex flex-col gap-6">
      <NextUpCard next={next} rows={patternRows(items, sessions, items.length)} lastTalk={lastTalk ? whenLabel(lastTalk, now) : null} />
      {windowLoaded && wordsLoaded && <>
          <TotalsTiles totals={totals({
        sessions: chartInput.sessions,
        lessonDays,
        words: chartInput.words
      })} />
          <ActivityCalendar grid={grid} />
        </>}
      <PatternList rows={rows} more={Math.max(0, items.length - rows.length)} />
      <QuietBoundary>
        <Suspense fallback={null}>
          <MomentsList register="adult" />
        </Suspense>
      </QuietBoundary>
      <Phrases phrases={view.phrases} title={t('progress-phrases')} />
      {hasWindow && <details className="group">
          <summary className="link link-primary min-h-(--hit-min) cursor-pointer text-sm">
            {t('progress-more-charts')}
          </summary>
          <div className="mt-4">
            <ProgressCharts charts={adultCharts(chartInput)} register="adult" />
          </div>
        </details>}
    </div>;
}